import React from 'react';
import { Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Field, Form, Formik } from 'formik';

import { number, password } from '../../../utils/validation';
import { FormikFieldRenderProps } from '@troy/shared/src/types/formik';

import {
  ActionButton,
  Translation,
  TextInput
} from '@troy/shared/src/components/common';

interface NewPasswordFormFields {
  code: string;
  password: string;
}

interface NewPasswordProps {
  onCancel: () => void;
  onSubmit: (values: NewPasswordFormFields) => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  form: {
    marginTop: theme.spacing(6),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%'
  },
  field: {
    width: '100%',

    '&:nth-of-type(1n+2)': {
      marginTop: theme.spacing(2)
    }
  },
  sendButton: {
    marginTop: theme.spacing(2),
    width: `calc(100% - ${theme.spacing(4)})`
  },
  cancelButton: {
    marginTop: theme.spacing(1),
    width: `calc(100% - ${theme.spacing(4)})`
  }
}));

const CodeField: React.FC<FormikFieldRenderProps> = ({
  field: { name, value, onChange }
}): React.ReactElement => {
  const classes = useStyles();

  return (
    <TextInput
      className={classes.field}
      id="page.forgot-password.component.password-form.forgot-password-code"
      placeholder="UNIC_LOGIN_CODE"
      inputPlaceholder="UNIC_LOGIN_CODE"
      startIcon="play"
      name={name}
      value={value}
      onChange={onChange}
      autoComplete="code"
    />
  );
};

const Password: React.FC<FormikFieldRenderProps> = ({
  field: { name, value, onChange }
}): React.ReactElement => {
  const classes = useStyles();

  return (
    <TextInput
      className={classes.field}
      id="page.forgot-password.component.password-form.forgot-password-pwd"
      placeholder="PASSWORD"
      inputPlaceholder="ALL_UXCOPIES_PLACEHOLDER_PASSWORD"
      startIcon="key"
      type="password"
      name={name}
      value={value}
      onChange={onChange}
      autoComplete="new-password"
    />
  );
};

const ForgotPasswordForm: React.FC<NewPasswordProps> = ({
  onSubmit,
  onCancel
}): React.ReactElement => {
  const classes = useStyles();

  return (
    <Formik
      validateOnMount
      onSubmit={onSubmit}
      initialValues={{ code: '', password: '' }}
    >
      {({ isValid }): JSX.Element => (
        <Form className={classes.form} autoComplete="off">
          {/* Disable auto-complete for Chrome */}
          <input
            autoComplete="false"
            name="hidden"
            type="text"
            style={{ display: 'none' }}
          />
          <Field name="code" component={CodeField} validate={number(6)} />
          <Field name="password" component={Password} validate={password} />
          <ActionButton
            id="page.forgot-password.component.password-form.forgot-password-button"
            type="submit"
            className={classes.sendButton}
            padding="medium"
            disabled={!isValid}
          >
            <Translation isFragment>UNIC_LOGIN_BUTTON_SEND</Translation>
          </ActionButton>
          <ActionButton
            id="page.forgot-password.component.password-form.forgot-password-cancel"
            className={classes.cancelButton}
            padding="medium"
            variant="outlined"
            color="warningDark"
            onClick={onCancel}
          >
            <Translation isFragment>ALL_UXCOPIES_BTN_CANCEL</Translation>
          </ActionButton>
        </Form>
      )}
    </Formik>
  );
};

export default ForgotPasswordForm;
